import { useChat } from '@ai-sdk/react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useAISDKRuntime } from '@assistant-ui/react-ai-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport, isTextUIPart, validateUIMessages } from 'ai';
import { Sidekick } from 'corso-types';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';
import api from '~/api';
import { useUserId } from '~/features/settings/developer/hooks/useUserId';
import { useStoreId } from '~/providers/StoreUserProvider';
import { usePageContext } from './usePageContext';

/** Not to be used directly, as it creates the state for context, which can then infer the type, and provide it safely through a separate hook.  */
function useCorsoAiChatContextValue() {
  const storeId = useStoreId();
  const userId = useUserId();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  // the transport reads this, so a conversation created mid-send is used without remounting the chat
  const conversationIdRef = useRef<number | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  const page = usePageContext();
  // dismissing the page applies to the page it was dismissed on
  const [isPageDismissed, setIsPageDismissed] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setIsPageDismissed(false), [pathname]);
  const sharedPage = isPageDismissed ? null : page;
  // the transport reads this, so each message carries the page it was asked from
  const sharedPageRef = useRef(sharedPage);
  sharedPageRef.current = sharedPage;

  // a conversation belongs to one store; switching stores starts fresh
  useEffect(() => {
    conversationIdRef.current = null;
    setConversationId(null);
  }, [storeId]);

  const conversationsQuery = useQuery({
    queryKey: ['ai-conversations', storeId, userId, conversationId], // current conversation ID include as a key to trigger refetch when it changes, to ensure we have the latest messages for it
    queryFn: () => api.store(storeId).conversations(userId).list(),
  });

  const conversationQuery = useQuery({
    queryKey: ['ai-conversation', storeId, userId, conversationId],
    enabled: !!conversationId, // can only query if it's known
    queryFn: () => {
      if (!conversationId) {
        throw new Error('No conversation ID'); // should be prevented by the `enabled` flag, but be defensive for typing
      }
      return api.store(storeId).conversations(userId).get(`${conversationId}`);
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: ({
      conversationId: feedbackConversationId,
      messageId,
      userSentiment,
      userComment,
    }: {
      conversationId: number;
      messageId: number;
      userSentiment: Sidekick.UserSentiment | null;
      userComment: string | null;
    }) =>
      api
        .store(storeId)
        .conversations(userId)
        .feedback(`${feedbackConversationId}`, `${messageId}`, {
          userSentiment,
          userComment,
        }),
    onSuccess: () => conversationQuery.refetch(), // refetch the conversation to get the updated feedback for the message
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.store(storeId).conversations(userId).rename(`${id}`, name),
    onSuccess: () =>
      Promise.all([
        conversationQuery.refetch(),
        queryClient.invalidateQueries({
          queryKey: ['ai-conversations', storeId, userId],
        }),
      ]),
  });

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: 'sidekick', // unused: `fetch` below sends through the typed api client
        // the server holds the history; send only the latest question (regenerate drops the answer after it first), flagging when its answer should be replaced
        prepareSendMessagesRequest: ({ messages, trigger }) => ({
          body: {
            message: messages.at(-1),
            regenerate: trigger === 'regenerate-message',
            ...(sharedPageRef.current && {
              pageContext: sharedPageRef.current,
            }),
          } satisfies Omit<Sidekick.ChatRequest, 'message'> & {
            message: unknown;
          },
        }),
        // the signal is Stop in the chat, which also ends the response on the server
        fetch: async (_input, init) => {
          const id =
            conversationIdRef.current ??
            (await api.store(storeId).conversations(userId).create())
              .conversationId;
          conversationIdRef.current = id;
          setConversationId(id);
          return api
            .store(storeId)
            .conversations(userId)
            .stream(
              `${id}`,
              Sidekick.chatRequestSchema.parse(
                JSON.parse(z.string().parse(init?.body)),
              ),
              init?.signal ?? undefined,
            );
        },
      }),
    [storeId, userId],
  );

  const chat = useChat({
    // a new chat per store, so switching stores clears the thread
    id: `sidekick-${storeId}`,
    transport,
    onFinish: () => {
      // picks up the generated conversation name and stored feedback state
      queryClient
        .invalidateQueries({ queryKey: ['ai-conversations', storeId, userId] })
        .catch(() => {});
      conversationQuery.refetch().catch(() => {});
    },
  });
  const runtime = useAISDKRuntime(chat);

  const isBusy = chat.status === 'submitted' || chat.status === 'streaming';

  /** Change the active conversation. */
  const changeConversation = useCallback(
    async (id: number | null) => {
      if (isBusy) return; // should not change conversations while a response is being streamed
      conversationIdRef.current = id;
      setConversationId(id);
      chat.clearError();
      if (!id) {
        chat.setMessages([]);
        return;
      }
      setIsLoadingConversation(true);
      try {
        const conversation = await queryClient.fetchQuery({
          queryKey: ['ai-conversation', storeId, userId, id],
          queryFn: () => api.store(storeId).conversations(userId).get(`${id}`),
        });
        chat.setMessages(
          await validateUIMessages({
            messages: conversation.messages.map(Sidekick.toUiMessage),
          }),
        );
      } finally {
        setIsLoadingConversation(false);
      }
    },
    [chat, isBusy, queryClient, storeId, userId],
  );

  /** Utility to start a new conversation with an initial message sent on behalf of the user. */
  const startConversation = useCallback(
    (content: string) => {
      if (isBusy) return; // cannot start a new conversation while another response is being streamed
      conversationIdRef.current = null;
      setConversationId(null);
      chat.clearError();
      chat.setMessages([]);
      chat.sendMessage({ text: content }).catch(() => {});
      setOpen(true);
    },
    [chat, isBusy],
  );

  // a request that failed before its answer started leaves the question unanswered, and possibly unsaved: ask it again
  const retry = useCallback(() => {
    const question = chat.messages.at(-1);
    if (question?.role !== 'user') {
      chat.regenerate().catch(() => {});
      return;
    }
    chat.setMessages(chat.messages.slice(0, -1));
    chat
      .sendMessage({
        text: question.parts
          .filter(isTextUIPart)
          .map((part) => part.text)
          .join(''),
      })
      .catch(() => {});
  }, [chat]);

  return useMemo(
    () => ({
      open,
      setOpen,

      runtime,
      isBusy,
      error: chat.error ?? null,

      page,
      sharedPage,
      dismissPage: () => setIsPageDismissed(true),

      retry,

      conversations: conversationsQuery.data ?? [],
      isLoadingConversations: conversationsQuery.isPending,
      conversation: conversationQuery.data ?? null,
      conversationId,
      isLoadingConversation,
      changeConversation,
      renameConversation: renameMutation.mutate,

      updateFeedback: feedbackMutation.mutate,

      startConversation,
    }),
    [
      changeConversation,
      chat.error,
      conversationId,
      conversationQuery.data,
      conversationsQuery.data,
      conversationsQuery.isPending,
      feedbackMutation.mutate,
      isBusy,
      isLoadingConversation,
      open,
      page,
      renameMutation.mutate,
      sharedPage,
      retry,
      runtime,
      startConversation,
    ],
  );
}

// TODO create a pattern/factor abstraction for creating strict contexts

const CorsoAiChatContext = createContext<ReturnType<
  typeof useCorsoAiChatContextValue
> | null>(null);

export function useCorsoAiChat() {
  const context = useContext(CorsoAiChatContext);
  if (!context) {
    throw new Error(
      'useCorsoAiChat must be used within a CorsoAiChatContextProvider',
    );
  }

  return context;
}

export function CorsoAiChatContextProvider({ children }: PropsWithChildren) {
  const value = useCorsoAiChatContextValue();
  return (
    <CorsoAiChatContext.Provider value={value}>
      <AssistantRuntimeProvider runtime={value.runtime}>
        {children}
      </AssistantRuntimeProvider>
    </CorsoAiChatContext.Provider>
  );
}
